import { Button } from '@/components/ui/button'
import { useBalance, useRenewalInfo } from '@/hooks/use-balance'
import { useSubscription } from '@/hooks/use-billing'
import { cn } from '@/lib/utils'
import { AlertCircle, AlertTriangle, Clock, Info, X } from 'lucide-react'
import * as React from 'react'
import { Link } from 'react-router-dom'

type BannerVariant = 'warning' | 'danger' | 'info'

interface BannerConfig {
  variant: BannerVariant
  icon: React.ElementType
  message: string
  cta?: { label: string; href: string }
}

function getBannerConfig(
  subscriptionStatus: string | undefined,
  balanceSufficient: boolean,
  renewalAmountCents: number,
  balanceCents: number,
  daysUntilRenewal?: number,
  trialDaysLeft?: number,
  unpaidDaysLeft?: number,
): BannerConfig | null {
  // PastDue — yellow warning
  if (subscriptionStatus === 'past_due') {
    const neededCents = renewalAmountCents - balanceCents
    if (neededCents > 0) {
      const needed = (neededCents / 100).toFixed(2)
      return {
        variant: 'warning',
        icon: AlertTriangle,
        message: `Your subscription payment failed. Add $${needed} to continue service.`,
        cta: { label: 'Top Up Now', href: '/billing?tab=balance&topup=true' },
      }
    }
    // Balance covers renewal but status is still past_due — retry pending
    return {
      variant: 'warning',
      icon: AlertTriangle,
      message: 'Your last payment attempt failed. We will retry automatically.',
      cta: { label: 'View Billing', href: '/billing' },
    }
  }

  // Unpaid — red danger
  if (subscriptionStatus === 'unpaid') {
    const daysText = unpaidDaysLeft != null ? ` in ${unpaidDaysLeft} days` : ' soon'
    return {
      variant: 'danger',
      icon: AlertCircle,
      message: `Your subscription will be cancelled${daysText}. Top up now to avoid losing access.`,
      cta: { label: 'Top Up Now', href: '/billing?tab=balance&topup=true' },
    }
  }

  // Trialing near end — info
  if (subscriptionStatus === 'trialing' && trialDaysLeft != null && trialDaysLeft <= 3) {
    return {
      variant: 'info',
      icon: Clock,
      message: `Your trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}. Add funds to continue after trial.`,
      cta: { label: 'Add Funds', href: '/billing?tab=balance&topup=true' },
    }
  }

  // Active but balance insufficient for next renewal — info
  // Suppress if renewal is more than 7 days away (just purchased)
  if (subscriptionStatus === 'active' && !balanceSufficient && renewalAmountCents > 0) {
    if (daysUntilRenewal != null && daysUntilRenewal > 7) {
      return null
    }
    const neededCents = renewalAmountCents - balanceCents
    if (neededCents <= 0) return null
    const needed = (neededCents / 100).toFixed(2)
    return {
      variant: 'info',
      icon: Info,
      message: `Your balance is insufficient for the next renewal. You need $${needed} more.`,
      cta: { label: 'Top Up', href: '/billing?tab=balance&topup=true' },
    }
  }

  return null
}

const variantStyles: Record<BannerVariant, { bg: string; border: string; text: string; iconBg: string }> = {
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-800/50',
    text: 'text-amber-800 dark:text-amber-200',
    iconBg: 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400',
  },
  danger: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800/50',
    text: 'text-red-800 dark:text-red-200',
    iconBg: 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800/50',
    text: 'text-blue-800 dark:text-blue-200',
    iconBg: 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400',
  },
}

export function BillingNotificationBanner() {
  const { data: subscription, isLoading: subLoading } = useSubscription()
  const { data: balance, isLoading: balLoading } = useBalance()
  const { data: renewalInfo, isLoading: renewalLoading } = useRenewalInfo()
  const [dismissed, setDismissed] = React.useState(false)

  // Don't render until billing data is loaded to avoid "$0.00" flash
  if (subLoading || balLoading || renewalLoading) return null
  if (dismissed) return null

  const balanceCents = balance?.balanceCents ?? 0
  const renewalAmountCents = renewalInfo?.amountCents ?? 0
  const balanceSufficient = renewalInfo?.balanceSufficient ?? true

  // Calculate trial days left
  let trialDaysLeft: number | undefined
  if (subscription?.status === 'trialing' && subscription.currentPeriodEnd) {
    const endDate = new Date(subscription.currentPeriodEnd)
    const now = new Date()
    trialDaysLeft = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
  }

  // Calculate days until next renewal
  let daysUntilRenewal: number | undefined
  if (subscription?.status === 'active' && subscription.currentPeriodEnd) {
    const endDate = new Date(subscription.currentPeriodEnd)
    const now = new Date()
    daysUntilRenewal = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
  }

  // Calculate unpaid days left (14 - days since past_due started, estimated)
  let unpaidDaysLeft: number | undefined
  if (subscription?.status === 'unpaid' && subscription.currentPeriodEnd) {
    const endDate = new Date(subscription.currentPeriodEnd)
    const now = new Date()
    const daysSinceEnd = Math.ceil((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24))
    unpaidDaysLeft = Math.max(0, 14 - daysSinceEnd)
  }

  const config = getBannerConfig(
    subscription?.status,
    balanceSufficient,
    renewalAmountCents,
    balanceCents,
    daysUntilRenewal,
    trialDaysLeft,
    unpaidDaysLeft,
  )

  if (!config) return null

  const Icon = config.icon
  const styles = variantStyles[config.variant]

  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 border-b px-4 py-2.5',
        styles.bg,
        styles.border,
        styles.text,
      )}
    >
      <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', styles.iconBg)}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <p className="flex-1 text-sm font-medium leading-snug">
        {config.message}
      </p>
      {config.cta && (
        <Button
          asChild
          size="sm"
          variant={config.variant === 'danger' ? 'destructive' : 'outline'}
          className="shrink-0 h-8 text-xs"
        >
          <Link to={config.cta.href}>{config.cta.label}</Link>
        </Button>
      )}
      <button
        type="button"
        className="shrink-0 rounded-md p-1 opacity-60 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-1"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
